import { formatExitCandidate } from './backtestStatusFormatters';
import type { BacktestParameterAnalysis } from './backtestStatusTypes';

export interface BreakdownRow {
    key: string;
    label: string;
    /** Hover text carrying the untruncated identity behind the label, e.g. the full exit candidate hash. */
    labelTitle?: string;
    /** Secondary line under the label, e.g. the option legs making up a combination. */
    sublabel?: string;
    backtestCount: number;
    totalTrades: number;
    tradeWinRatePercent: number;
    avgTradeReturnPercent: number;
    avgCumulativeReturnPercent: number;
}

type CombinationParameters = BacktestParameterAnalysis['combinations'][number]['combination'];

type ExitCandidateIdentity = { exitCandidateId: number | null; exitCandidateHash: string | null };

/**
 * Backtests are keyed by exit candidate id + hash, so the id stays on screen even when the strategy
 * has a friendly label for the candidate. Unlabeled candidates fall back to "Candidate 55833 (hash…)".
 */
const resolveExitCandidateLabel = (candidate: ExitCandidateIdentity, exitCandidateLabelById: Map<number, string>): string => {
    const configuredLabel = candidate.exitCandidateId !== null ? exitCandidateLabelById.get(candidate.exitCandidateId)?.trim() : undefined;

    return configuredLabel
        ? `${configuredLabel} (#${candidate.exitCandidateId})`
        : formatExitCandidate({ candidateId: candidate.exitCandidateId, candidateHash: candidate.exitCandidateHash });
};

/** Two candidates can share an id across hashes, so the full hash is kept in the cell tooltip. */
const resolveExitCandidateTitle = (candidate: ExitCandidateIdentity, label: string): string =>
    candidate.exitCandidateHash ? `${label} · hash ${candidate.exitCandidateHash}` : label;

/** Option buying strategy label, always carrying the id the backtest rows are keyed by. */
const resolveOptionStrategyLabel = (strategyId: number, optionStrategyNameById: Map<number, string>): string => {
    const name = optionStrategyNameById.get(strategyId)?.trim();

    return name ? `${name} (#${strategyId})` : `Strategy #${strategyId}`;
};

const getOptionStrategyIds = (combination: CombinationParameters): number[] =>
    [
        combination.optionBuyingStrategyIdLeg1,
        combination.optionBuyingStrategyIdLeg2,
        combination.optionBuyingStrategyIdLeg3,
        combination.optionBuyingStrategyIdLeg4,
        combination.optionBuyingStrategyIdLeg5,
        combination.optionBuyingStrategyIdLeg6
    ].filter((strategyId): strategyId is number => strategyId !== null && strategyId !== undefined);

const byBacktestCountDescending = (first: BreakdownRow, second: BreakdownRow): number => second.backtestCount - first.backtestCount;

export const buildExitCandidateRows = (
    analysis: BacktestParameterAnalysis | undefined,
    exitCandidateLabelById: Map<number, string>
): BreakdownRow[] => {
    if (!analysis) return [];

    return analysis.byExitCandidate
        .map((row) => {
            const label = resolveExitCandidateLabel(row, exitCandidateLabelById);

            return {
                key: `exit-${row.exitCandidateId ?? 'null'}-${row.exitCandidateHash ?? 'null'}`,
                label,
                labelTitle: resolveExitCandidateTitle(row, label),
                backtestCount: row.backtestCount,
                totalTrades: row.totalTrades ?? 0,
                tradeWinRatePercent: row.tradeWinRatePercent,
                avgTradeReturnPercent: row.avgTradeReturnPercent,
                avgCumulativeReturnPercent: row.avgPercentageGain
            };
        })
        .sort(byBacktestCountDescending);
};

export const buildOptionStrategyRows = (
    analysis: BacktestParameterAnalysis | undefined,
    optionStrategyNameById: Map<number, string>
): BreakdownRow[] => {
    if (!analysis) return [];

    return analysis.byOptionStrategy
        .map((row) => ({
            key: `option-${row.strategyId}`,
            label: resolveOptionStrategyLabel(row.strategyId, optionStrategyNameById),
            backtestCount: row.backtestCount,
            totalTrades: row.totalTrades ?? 0,
            tradeWinRatePercent: row.tradeWinRatePercent,
            avgTradeReturnPercent: row.avgTradeReturnPercent,
            avgCumulativeReturnPercent: row.avgPercentageGain
        }))
        .sort(byBacktestCountDescending);
};

/**
 * A combination is one exit rule paired with the option strategy legs it ran with. Ranked best-first by
 * avg cumulative return so the strongest pairing leads, rather than the most-backtested one.
 */
export const buildCombinationRows = (
    analysis: BacktestParameterAnalysis | undefined,
    exitCandidateLabelById: Map<number, string>,
    optionStrategyNameById: Map<number, string>
): BreakdownRow[] => {
    if (!analysis) return [];

    return analysis.combinations
        .map((row) => {
            const optionStrategyIds = getOptionStrategyIds(row.combination);
            const label = resolveExitCandidateLabel(row.combination, exitCandidateLabelById);

            return {
                key: `combination-${row.combination.exitCandidateId ?? 'null'}-${row.combination.exitCandidateHash ?? 'null'}-${
                    optionStrategyIds.join('-') || 'none'
                }`,
                label,
                labelTitle: resolveExitCandidateTitle(row.combination, label),
                sublabel: optionStrategyIds.length
                    ? optionStrategyIds.map((strategyId) => resolveOptionStrategyLabel(strategyId, optionStrategyNameById)).join(' + ')
                    : 'No option strategy',
                backtestCount: row.backtestCount,
                totalTrades: row.totalTrades ?? 0,
                tradeWinRatePercent: row.tradeWinRatePercent,
                avgTradeReturnPercent: row.avgAvgTradeProfit,
                avgCumulativeReturnPercent: row.avgPercentageGain
            };
        })
        .sort(
            (first, second) =>
                second.avgCumulativeReturnPercent - first.avgCumulativeReturnPercent || second.backtestCount - first.backtestCount
        );
};
